/**
 * Executive summary.
 *
 * Defaults to the approved paragraph with the client name substituted — exactly
 * what a Quick Proposal renders — so Sales never starts from a blank page.
 *
 * Customising is deliberate and reversible. A generated suggestion is *shown*
 * first and never written over the current text until Apply is pressed: losing
 * a rewritten summary to an automatic overwrite would be the kind of thing that
 * makes people stop trusting the tool.
 */
import { useState } from 'react';
import type { AIStatus } from '../../lib/api';
import { Button, Card } from '../../components/ui';
import { AIAssist } from './AIAssist';

export function ExecutiveSummaryEditor({
  value,
  approvedDefault,
  onChange,
  onRestore,
  disabled,
  proposalId,
  aiStatus,
}: {
  /** The override, or undefined when the approved default is in use. */
  value: string | undefined;
  approvedDefault: string;
  onChange(next: string): void;
  onRestore(): void;
  disabled: boolean;
  proposalId: string;
  /** Null, or disabled, hides the AI controls entirely. */
  aiStatus: AIStatus | null;
}) {
  const [editing, setEditing] = useState(false);

  const isCustomised = value !== undefined && value.trim() !== approvedDefault.trim();
  const current = value ?? approvedDefault;

  return (
    <Card
      title="Executive summary"
      description="The approved paragraph, with the client name filled in."
      actions={
        isCustomised ? (
          <span className="rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800 ring-1 ring-amber-200">
            Modified
          </span>
        ) : (
          <span className="rounded bg-tint px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted">
            Standard
          </span>
        )
      }
    >
      {!editing ? (
        <>
          <p className="whitespace-pre-wrap text-[13px] text-body">{current}</p>

          <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-rule pt-3">
            <Button
              variant="secondary"
              size="sm"
              disabled={disabled}
              onClick={() => setEditing(true)}
            >
              Customize executive summary
            </Button>
            {isCustomised && (
              <Button variant="ghost" size="sm" disabled={disabled} onClick={onRestore}>
                Restore standard
              </Button>
            )}
          </div>
        </>
      ) : (
        <div className="space-y-4">
          <label className="block">
            <span className="mb-1 block text-[12px] font-medium text-ink">
              Edit manually
            </span>
            <textarea
              rows={6}
              maxLength={4000}
              autoFocus
              disabled={disabled}
              value={current}
              onChange={(event) => onChange(event.target.value)}
              className="w-full rounded-md border border-rule bg-white px-3 py-2 text-sm text-ink focus:border-teal focus:outline-none focus:ring-2 focus:ring-teal/20 disabled:bg-tint"
            />
            <span className="mt-1 block text-[11px] text-muted">
              {current.length} / 4000 characters
            </span>
          </label>

          {aiStatus?.enabled ? (
            <AIAssist
              proposalId={proposalId}
              status={aiStatus}
              label="Optional AI assistance"
              functions={[
                'generateClientExecutiveSummary',
                'improveExecutiveSummary',
                'makeMoreExecutive',
                'makeMoreConsultative',
                'shortenSection',
                'proofreadProposal',
              ]}
              currentText={current}
              disabled={disabled}
              onApply={onChange}
            />
          ) : (
            <p className="rounded-md border border-rule px-3.5 py-3 text-[12px] text-muted">
              An administrator can enable optional AI assistance for this section. The
              approved summary above is complete without it.
            </p>
          )}

          <div className="flex flex-wrap items-center gap-2 border-t border-rule pt-3">
            <Button variant="secondary" size="sm" onClick={() => setEditing(false)}>
              Done
            </Button>
            {isCustomised && (
              <Button
                variant="ghost"
                size="sm"
                disabled={disabled}
                onClick={onRestore}
              >
                Restore standard
              </Button>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}
